/**
 * Browser UI language (navbar, t(), X-App-Locale) — per logged-in user, separate from account prefs in LS.
 * Account keys: preferred_language_user / preferred_language_company (JWT/startRequest/profile only).
 */

const LEGACY_GLOBAL_LOCALE_KEY = 'locale';
export const UI_LOCALE_PREFIX = 'app_ui_locale:';

export const normalizeAppLocale = (value) => {
  const v = String(value ?? '').trim().toLowerCase();
  if (v === 'gb') return 'en';
  return ['sk', 'en', 'de'].includes(v) ? v : '';
};

export const uiLocaleStorageKey = (userId) => `${UI_LOCALE_PREFIX}${String(userId)}`;

const accountTokenFromUser = (u) => normalizeAppLocale(u?.preferred_language);
const companyTokenFromUser = (u) => normalizeAppLocale(u?.company_preferred_language);

/**
 * One-time: move legacy global `locale` into per-user storage for the given id, then remove legacy key.
 */
export const migrateLegacyGlobalLocaleToUser = (userId) => {
  if (typeof window === 'undefined' || !window.localStorage || userId == null) return;
  const legacy = normalizeAppLocale(localStorage.getItem(LEGACY_GLOBAL_LOCALE_KEY));
  if (!legacy) return;
  const key = uiLocaleStorageKey(userId);
  if (!normalizeAppLocale(localStorage.getItem(key))) {
    localStorage.setItem(key, legacy);
  }
  localStorage.removeItem(LEGACY_GLOBAL_LOCALE_KEY);
};

export const getStoredUiLocaleForUser = (userId) => {
  if (typeof window === 'undefined' || !window.localStorage || userId == null) return '';
  migrateLegacyGlobalLocaleToUser(userId);
  return normalizeAppLocale(localStorage.getItem(uiLocaleStorageKey(userId)));
};

export const setStoredUiLocaleForUser = (userId, code) => {
  if (typeof window === 'undefined' || !window.localStorage || userId == null) return;
  const normalized = normalizeAppLocale(code) || 'sk';
  localStorage.setItem(uiLocaleStorageKey(userId), normalized);
};

/**
 * UI language for the app shell: per-user cache, then account fields from auth object (not LS account keys).
 * @param { { id?: number|string, preferred_language?: string|null, company_preferred_language?: string|null } | null } user
 */
export const resolveUiLocale = (user) => {
  if (typeof window === 'undefined' || !window.localStorage) return 'sk';
  const id = user?.id;
  if (id != null) {
    migrateLegacyGlobalLocaleToUser(id);
    const stored = normalizeAppLocale(localStorage.getItem(uiLocaleStorageKey(id)));
    if (stored) return stored;
  }
  const fromAccount = accountTokenFromUser(user);
  if (fromAccount) return fromAccount;
  const fromCompany = companyTokenFromUser(user);
  if (fromCompany) return fromCompany;
  return 'sk';
};

export const removeLegacyGlobalLocaleKey = () => {
  if (typeof window === 'undefined' || !window.localStorage) return;
  localStorage.removeItem(LEGACY_GLOBAL_LOCALE_KEY);
};
